package com.shadowings.gero.presentation.screen

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CheckboxDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ElevatedCard
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TopAppBar
import androidx.compose.material3.TopAppBarDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.semantics.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.navigation.NavController
import androidx.navigation.compose.rememberNavController
import com.shadowings.gero.domain.repository.AnalyticsRepository
import com.shadowings.gero.domain.repository.AuthRepository
import com.shadowings.gero.presentation.component.GeroButton
import com.shadowings.gero.presentation.ui.AppTheme
import com.shadowings.gero.presentation.ui.GoogleIcon
import com.shadowings.gero.presentation.util.DevicePreviews
import com.shadowings.gero.presentation.viewmodel.AuthViewModel
import compose.icons.FeatherIcons
import compose.icons.feathericons.ChevronLeft
import compose.icons.feathericons.Cloud
import compose.icons.feathericons.Eye
import compose.icons.feathericons.EyeOff
import compose.icons.feathericons.Lock
import compose.icons.feathericons.Mail
import dev.gitlive.firebase.auth.FirebaseUser
import gero.composeapp.generated.resources.Res
import gero.composeapp.generated.resources.accept_terms_label
import gero.composeapp.generated.resources.already_have_account
import gero.composeapp.generated.resources.back_btn_desc
import gero.composeapp.generated.resources.cloud_save_desc
import gero.composeapp.generated.resources.cloud_save_title
import gero.composeapp.generated.resources.confirm_password_label
import gero.composeapp.generated.resources.create_account_title
import gero.composeapp.generated.resources.email_label
import gero.composeapp.generated.resources.email_placeholder
import gero.composeapp.generated.resources.login_btn
import gero.composeapp.generated.resources.login_with_google
import gero.composeapp.generated.resources.new_user_register_here
import gero.composeapp.generated.resources.or_divider
import gero.composeapp.generated.resources.password_label
import gero.composeapp.generated.resources.privacy_policy_label
import gero.composeapp.generated.resources.register_btn
import gero.composeapp.generated.resources.terms_and_conditions_label
import com.shadowings.gero.util.systemLanguageCode
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun AuthScreen(
    navController: NavController,
    viewModel: AuthViewModel = koinViewModel()
) {
    val email by viewModel.email.collectAsStateWithLifecycle()
    val password by viewModel.password.collectAsStateWithLifecycle()
    val confirmPassword by viewModel.confirmPassword.collectAsStateWithLifecycle()
    val isRegistering by viewModel.isRegistering.collectAsStateWithLifecycle()
    val isLoading by viewModel.isLoading.collectAsStateWithLifecycle()
    val error by viewModel.error.collectAsStateWithLifecycle()
    val success by viewModel.success.collectAsStateWithLifecycle()
    val termsAccepted by viewModel.termsAccepted.collectAsStateWithLifecycle()

    var passwordVisible by remember { mutableStateOf(false) }
    val uriHandler = LocalUriHandler.current
    LaunchedEffect(success) {
        if (success) {
            viewModel.resetSuccess()
            navController.popBackStack()
        }
    }

    Scaffold(
        topBar = {
            TopAppBar(
                title = {
                    Text(
                        text = if (isRegistering) stringResource(Res.string.create_account_title) else stringResource(
                            Res.string.login_btn
                        ),
                        style = MaterialTheme.typography.titleLarge.copy(fontWeight = FontWeight.Bold),
                        color = MaterialTheme.colorScheme.onSurface
                    )
                },
                navigationIcon = {
                    IconButton(
                        onClick = { navController.popBackStack() },
                        modifier = Modifier.semantics { testTag = "auth_btn_back" }
                    ) {
                        Icon(
                            imageVector = FeatherIcons.ChevronLeft,
                            contentDescription = stringResource(Res.string.back_btn_desc),
                            tint = MaterialTheme.colorScheme.onSurface
                        )
                    }
                },
                colors = TopAppBarDefaults.topAppBarColors(
                    containerColor = Color.Transparent
                )
            )
        },
        containerColor = Color.Transparent
    ) { paddingValues ->
        Box(
            modifier = Modifier
                .fillMaxSize()
                .background(MaterialTheme.colorScheme.background)
                .padding(paddingValues),
            contentAlignment = Alignment.Center
        ) {
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(24.dp)
                    .verticalScroll(rememberScrollState()),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.Center
            ) {
                // Standard Material Design Card
                ElevatedCard(
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(12.dp),
                ) {
                    Column(
                        modifier = Modifier.padding(20.dp),
                        verticalArrangement = Arrangement.SpaceEvenly,
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        // Title / Description
                        Icon(
                            imageVector = FeatherIcons.Cloud,
                            contentDescription = null,
                            tint = MaterialTheme.colorScheme.primary,
                            modifier = Modifier.size(48.dp)
                        )

                        Text(
                            text = stringResource(Res.string.cloud_save_title),
                            style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.Bold),
                            color = MaterialTheme.colorScheme.onSurface
                        )

                        Text(
                            text = stringResource(Res.string.cloud_save_desc),
                            style = MaterialTheme.typography.bodyMedium,
                            textAlign = TextAlign.Center,
                            color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7f)
                        )

                        Spacer(Modifier.height(8.dp))

                        // Email Field
                        OutlinedTextField(
                            value = email,
                            onValueChange = { viewModel.setEmail(it) },
                            label = { Text(stringResource(Res.string.email_label)) },
                            placeholder = { Text(stringResource(Res.string.email_placeholder)) },
                            leadingIcon = {
                                Icon(
                                    imageVector = FeatherIcons.Mail,
                                    contentDescription = null,
                                    tint = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.5f)
                                )
                            },
                            singleLine = true,
                            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Email),
                            shape = RoundedCornerShape(12.dp),
                            modifier = Modifier.fillMaxWidth()
                        )

                        // Password Field
                        OutlinedTextField(
                            value = password,
                            onValueChange = { viewModel.setPassword(it) },
                            label = { Text(stringResource(Res.string.password_label)) },
                            leadingIcon = {
                                Icon(
                                    imageVector = FeatherIcons.Lock,
                                    contentDescription = null,
                                    tint = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.5f)
                                )
                            },
                            trailingIcon = {
                                val image =
                                    if (passwordVisible) FeatherIcons.EyeOff else FeatherIcons.Eye
                                IconButton(
                                    onClick = { passwordVisible = !passwordVisible },
                                    modifier = Modifier.semantics { testTag = "auth_btn_toggle_password_visibility" }
                                ) {
                                    Icon(imageVector = image, contentDescription = null)
                                }
                            },
                            singleLine = true,
                            visualTransformation = if (passwordVisible) VisualTransformation.None else PasswordVisualTransformation(),
                            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Password),
                            shape = RoundedCornerShape(12.dp),
                            modifier = Modifier.fillMaxWidth()
                        )

                        // Confirm Password Field (Register mode only)
                        AnimatedVisibility(visible = isRegistering) {
                            Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
                                OutlinedTextField(
                                    value = confirmPassword,
                                    onValueChange = { viewModel.setConfirmPassword(it) },
                                    label = { Text(stringResource(Res.string.confirm_password_label)) },
                                    leadingIcon = {
                                        Icon(
                                            imageVector = FeatherIcons.Lock,
                                            contentDescription = null,
                                            tint = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.5f)
                                        )
                                    },
                                    singleLine = true,
                                    visualTransformation = PasswordVisualTransformation(),
                                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Password),
                                    shape = RoundedCornerShape(12.dp),
                                    modifier = Modifier.fillMaxWidth()
                                )

                                // Terms and Conditions Checkbox
                                Row(
                                    modifier = Modifier.fillMaxWidth(),
                                    verticalAlignment = Alignment.Top,
                                    horizontalArrangement = Arrangement.Start
                                ) {
                                    Checkbox(
                                        checked = termsAccepted,
                                        onCheckedChange = { viewModel.setTermsAccepted(it) },
                                        colors = CheckboxDefaults.colors(
                                            checkedColor = MaterialTheme.colorScheme.primary,
                                            uncheckedColor = MaterialTheme.colorScheme.onSurface.copy(
                                                alpha = 0.6f
                                            )
                                        )
                                    )
                                    Spacer(Modifier.width(4.dp))
                                    Column(
                                        modifier = Modifier.weight(1f).padding(top = 12.dp)
                                    ) {
                                        Text(
                                            text = stringResource(Res.string.accept_terms_label),
                                            style = MaterialTheme.typography.bodySmall,
                                            color = MaterialTheme.colorScheme.onSurface
                                        )
                                        Spacer(Modifier.height(8.dp))
                                        val systemLang = systemLanguageCode()
                                        val legalLanguageSuffix = when {
                                            systemLang.startsWith("it") -> "it"
                                            systemLang.startsWith("de") -> "de"
                                            systemLang.startsWith("es") -> "es"
                                            systemLang.startsWith("fr") -> "fr"
                                            else -> "en"
                                        }
                                        Row(
                                            horizontalArrangement = Arrangement.spacedBy(16.dp)
                                        ) {
                                            TextButton(
                                                onClick = {
                                                    uriHandler.openUri("https://emmanuelevilla.com/miulingo/privacy_policy_${legalLanguageSuffix}.html")
                                                },
                                                contentPadding = PaddingValues(0.dp),
                                                modifier = Modifier.semantics { testTag = "auth_btn_privacy" }
                                            ) {
                                                Text(
                                                    text = stringResource(Res.string.privacy_policy_label),
                                                    style = MaterialTheme.typography.bodySmall.copy(
                                                        fontWeight = FontWeight.SemiBold
                                                    ),
                                                    color = MaterialTheme.colorScheme.primary
                                                )
                                            }
                                            TextButton(
                                                onClick = {
                                                    uriHandler.openUri("https://emmanuelevilla.com/miulingo/terms_and_conditions_${legalLanguageSuffix}.html")
                                                },
                                                contentPadding = PaddingValues(0.dp),
                                                modifier = Modifier.semantics { testTag = "auth_btn_terms" }
                                            ) {
                                                Text(
                                                    text = stringResource(Res.string.terms_and_conditions_label),
                                                    style = MaterialTheme.typography.bodySmall.copy(
                                                        fontWeight = FontWeight.SemiBold
                                                    ),
                                                    color = MaterialTheme.colorScheme.primary
                                                )
                                            }
                                        }
                                    }
                                }
                            }
                        }

                        // Error Box
                        AnimatedVisibility(
                            visible = error != null,
                            enter = fadeIn(),
                            exit = fadeOut()
                        ) {
                            error?.let { err ->
                                Card(
                                    colors = CardDefaults.cardColors(
                                        containerColor = MaterialTheme.colorScheme.errorContainer
                                    ),
                                    shape = RoundedCornerShape(8.dp),
                                    modifier = Modifier.fillMaxWidth()
                                ) {
                                    Text(
                                        text = err,
                                        style = MaterialTheme.typography.bodySmall.copy(fontWeight = FontWeight.Bold),
                                        color = MaterialTheme.colorScheme.onErrorContainer,
                                        modifier = Modifier.padding(12.dp),
                                        textAlign = TextAlign.Center
                                    )
                                }
                            }
                        }

                        Spacer(Modifier.height(8.dp))

                        // Submit Button
                        if (isLoading) {
                            CircularProgressIndicator(
                                color = MaterialTheme.colorScheme.primary,
                                modifier = Modifier.size(36.dp)
                            )
                        } else {
                            GeroButton(
                                title = if (isRegistering) stringResource(Res.string.register_btn) else stringResource(
                                    Res.string.login_btn
                                ),
                                testTagId = if (isRegistering) "auth_btn_register" else "auth_btn_login",
                                modifier = Modifier.fillMaxWidth(),
                                backgroundColor = MaterialTheme.colorScheme.primaryContainer,
                                borderColor = MaterialTheme.colorScheme.primary,
                                textColor = MaterialTheme.colorScheme.onPrimaryContainer,
                                onClick = { viewModel.submit() }
                            )

                            Row(
                                modifier = Modifier.fillMaxWidth().padding(vertical = 8.dp),
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Box(
                                    modifier = Modifier
                                        .weight(1f)
                                        .height(1.dp)
                                        .background(MaterialTheme.colorScheme.onSurface.copy(alpha = 0.12f))
                                )
                                Text(
                                    text = stringResource(Res.string.or_divider),
                                    style = MaterialTheme.typography.bodySmall,
                                    color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.5f),
                                    modifier = Modifier.padding(horizontal = 16.dp)
                                )
                                Box(
                                    modifier = Modifier
                                        .weight(1f)
                                        .height(1.dp)
                                        .background(MaterialTheme.colorScheme.onSurface.copy(alpha = 0.12f))
                                )
                            }

                            GeroButton(
                                testTagId = "auth_btn_google",
                                modifier = Modifier.fillMaxWidth(),
                                backgroundColor = MaterialTheme.colorScheme.surface,
                                borderColor = MaterialTheme.colorScheme.outline.copy(alpha = 0.3f),
                                onClick = { viewModel.signInWithGoogle() }
                            ) {
                                Row(
                                    verticalAlignment = Alignment.CenterVertically,
                                    horizontalArrangement = Arrangement.Center
                                ) {
                                    Icon(
                                        imageVector = GoogleIcon,
                                        contentDescription = null,
                                        modifier = Modifier.size(24.dp),
                                        tint = Color.Unspecified
                                    )
                                    Spacer(Modifier.width(12.dp))
                                    Text(
                                        text = stringResource(Res.string.login_with_google),
                                        style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.Bold),
                                        color = MaterialTheme.colorScheme.onSurface
                                    )
                                }
                            }
                        }

                        // Toggle Mode Button
                        TextButton(
                            onClick = { viewModel.toggleMode() },
                            modifier = Modifier
                                .fillMaxWidth()
                                .semantics { testTag = "auth_btn_toggle_mode" }
                        ) {
                            Text(
                                text = if (isRegistering) stringResource(Res.string.already_have_account) else stringResource(
                                    Res.string.new_user_register_here
                                ),
                                style = MaterialTheme.typography.bodyMedium.copy(fontWeight = FontWeight.SemiBold)
                            )
                        }
                    }
                }
            }
        }
    }
}

@DevicePreviews
@Preview
@Preview(name = "Dark", uiMode = 0x20, showBackground = true)
@Composable
private fun AuthScreenPreview() {
    AppTheme {
        Surface {
            AuthScreen(
                navController = rememberNavController(),
                viewModel = AuthViewModel(
                    authRepository = object : AuthRepository {
                        override val currentUser = kotlinx.coroutines.flow.flowOf(null)

                        override suspend fun signInWithEmail(email: String, password: String) =
                            Result.failure<FirebaseUser>(Exception())

                        override suspend fun signUpWithEmail(email: String, password: String) =
                            Result.failure<FirebaseUser>(Exception())

                        override suspend fun signInWithGoogle() =
                            Result.failure<FirebaseUser>(Exception())

                        override suspend fun signOut() = Result.success(Unit)
                    },
                    analyticsRepository = object : AnalyticsRepository {
                        override fun logEvent(name: String, params: Map<String, Any>) {}
                        override fun logStartLesson(lessonId: String) {}
                        override fun logStartQuiz(lessonId: String) {}
                        override fun logWordViewed(wordId: String, meaning: String) {}
                        override fun logHieroViewed(hieroId: String, unicode: String) {}
                        override fun logQuizCompleted(
                            lessonId: String,
                            score: Int,
                            total: Int,
                            errors: Int
                        ) {
                        }

                        override fun logStatClicked(statType: String) {}
                        override fun logFavoriteToggled(wordId: String, isFavorite: Boolean) {}
                        override fun logCharacterCustomized(
                            character: String,
                            category: String,
                            colorIndex: Int
                        ) {
                        }

                        override fun logCharacterReset(character: String) {}
                        override fun logSignOut() {}
                        override fun logTestModeToggled(enabled: Boolean) {}
                        override fun logDataReset() {}
                        override fun logTutorialStarted() {}
                        override fun logTutorialCompleted() {}
                        override fun logSignUp() {}
                        override fun logLogin() {}
                    }
                )
            )
        }
    }
}
